import './input-auto-complete-field.scss';

import { kebabCase } from 'lodash';
import {
  ChangeEvent,
  ComponentPropsWithRef,
  MouseEvent,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { dispatchNativeEvent } from '../../../data/utils/dispatch-native-event';
import { formatTestId } from '../../../data/utils/string.utils';
import { useContextDropdownList } from '../../../data/utils/use-context-dropdown-list';
import {
  ContextDropdownList,
  ContextDropdownListItem,
} from '../../context-menu/context-dropdown-list';
import { Spinner } from '../../spinner/spinner';
import { BaseInput, BaseInputIcon } from './base-input';

type InputAutoCompleteFieldProps = ComponentPropsWithRef<typeof BaseInput> & {
  className?: string;
  label?: JSX.Element | string;
  description?: JSX.Element | string;
  prefix?: JSX.Element | string;
  suffix?: JSX.Element | string;
  icon?: BaseInputIcon;
  error?: string | { error: string; [key: string]: string };
  value?: string;
  option?: string;
  options: ContextDropdownListItem[];
  optionsGrouped?: boolean;
  loading?: boolean;
  onSearch?: (value: string) => void;
};

export function InputAutoCompleteField({
  testId,
  className,
  label,
  description,
  prefix,
  suffix,
  icon = BaseInputIcon.SEARCH,
  error,
  options = [],
  optionsGrouped = false,
  loading,
  onSearch,
  ...props
}: InputAutoCompleteFieldProps) {
  const intl = useIntl();

  const getTestId = useCallback(
    (testIdType: string | string[] | null = null) =>
      formatTestId('input-field', testId ?? props.name, testIdType),
    [testId, props.name]
  );

  const [inputValue, setInputValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(props.value ?? '');
  }, [props.value]);

  useEffect(() => {
    setSearchValue(props.option ?? '');
  }, [props.option]);

  const { contextDropdownListState, onOpenDropdownList, onCloseDropdownList } =
    useContextDropdownList();

  const onHandleFocus = useCallback(() => {
    dispatchNativeEvent(inputRef.current, ['focus']);
  }, []);

  const onHandleChange = useCallback(
    (value: string, e: ChangeEvent<HTMLElement>) => {
      setSearchValue(value);

      onSearch?.(value);

      onOpenDropdownList(e as unknown as UIEvent);
    },
    [onSearch, onOpenDropdownList]
  );

  const onHandleBlur = useCallback(() => {
    onCloseDropdownList();

    dispatchNativeEvent(inputRef.current, ['blur']);
  }, [onCloseDropdownList]);

  const onHandleClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      onOpenDropdownList(e as unknown as UIEvent);

      dispatchNativeEvent(inputRef.current, ['click']);
    },
    [onOpenDropdownList]
  );

  const onHandleSelect = useCallback((value: ContextDropdownListItem) => {
    setInputValue(value.value);
    setSearchValue(value.title);

    dispatchNativeEvent(inputRef.current, ['input', 'blur'], {
      value: value.value,
    });
  }, []);

  const onHandleReset = useCallback(() => {
    setInputValue('');
    setSearchValue('');

    dispatchNativeEvent(inputRef.current, ['input', 'blur'], {
      value: '',
    });
  }, []);

  return (
    <div
      data-test-id={getTestId()}
      className={`form-group form-group--auto-complete${
        className ? ` form-group--${className}` : ''
      }${error ? ' invalid' : ''}${props.disabled ? ' disabled' : ''}`}
    >
      {label && (
        <label data-test-id={getTestId('label')} htmlFor={props.name}>
          {label}
        </label>
      )}

      {description && (
        <div
          data-test-id={getTestId('description')}
          className="input-description"
        >
          <p>{description}</p>
        </div>
      )}

      <div className="input-group">
        {prefix && (
          <span
            data-test-id={getTestId('prefix')}
            className="form-control-prefix"
          >
            {prefix}
          </span>
        )}
        {suffix && (
          <span
            data-test-id={getTestId('suffix')}
            className="form-control-suffix"
          >
            {suffix}
          </span>
        )}

        {icon !== BaseInputIcon.NONE && (
          <i
            data-test-id={getTestId('icon')}
            className={`icon ${kebabCase(icon)}`}
          />
        )}

        <div className="form-auto-complete-control">
          <BaseInput
            testId={getTestId('input-search')}
            type="search"
            name={`${props.name}-search`}
            value={searchValue}
            disabled={!!inputValue || props.disabled}
            onFocus={onHandleFocus}
            onChange={onHandleChange}
            onBlur={onHandleBlur}
            onClick={onHandleClick}
            ref={(props.ref as Ref<HTMLInputElement>) ?? undefined}
          />

          <BaseInput
            {...props}
            testId={getTestId('input')}
            type="text"
            name={`${props.name}`}
            value={inputValue}
            ref={inputRef}
          />

          {inputValue && (
            <button
              data-test-id={getTestId('clear')}
              type="button"
              className="form-control-clear"
              onClick={onHandleReset}
            />
          )}

          {loading && <Spinner />}
        </div>

        <ContextDropdownList
          state={contextDropdownListState}
          items={options}
          grouped={optionsGrouped}
          onSelect={onHandleSelect}
        />

        {error && (
          <div data-test-id={getTestId('error')} className="input-error">
            {intl.formatMessage(
              {
                id: typeof error === 'string' ? error : error.error,
              },
              typeof error === 'string' ? {} : error
            )}
          </div>
        )}
      </div>
    </div>
  );
}
